import { createBrowserHistory } from "history";
import { reaction } from "mobx";
import { createTheme, MuiThemeProvider } from "@material-ui/core/styles";
import { Provider } from "mobx-react";
import { syncHistoryWithStore } from "mobx-react-router";
import {
  Auth,
  Security,
  ImplicitCallback,
  SecureRoute
} from "@okta/okta-react";
import React from "react";
import { Router, Route } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import Rules from "pages/Rules";
import VideoEditor from "pages/VideoEditor";
import CustomAdHocClipEditor from "./pages/CustomAdHocClipEditor";
import CustomGameClipEditor from "./pages/CustomGameClipEditor";
import Clips from "./pages/Clips";
import Login from "./components/Login";
import ClippyApi from "httpClients/ClippyApi";
import PitsApi from "httpClients/PitsApi";
import SportyApi from "httpClients/SportyApi";
import SportyGateway from "./httpClients/SportyGateway";
import StatsApi from "httpClients/StatsApi";
import JaredApi from "httpClients/JaredApi";
import CerebroApi from "httpClients/CerebroApi";
import BasicSearchStore from "stores/BasicSearchStore";
import TrimPlayDialogStore from "stores/TrimPlayDialogStore";
import MatchupsDialogStore from "stores/MatchupsDialogStore";
import PitsMetadataStore from "stores/PitsMetadataStore";
import SportyMetadataStore from "stores/SportyMetadataStore";
import SportsReferenceStore from "stores/SportsReferenceStore";
import ReelStore from "stores/ReelStore";
import RouterStore from "stores/RouterStore";
import RuleStore from "stores/RuleStore";
import RulesStore from "stores/RulesStore";
import VideoPlayerStore from "stores/VideoPlayerStore";
import IssueDialogStore from "stores/IssueDialogStore";
import VideoSearchStore from "stores/VideoSearchStore";
import SendToForgeDialogStore from "stores/SendToForgeDialogStore";
import ForgeSnackbarStore from "stores/ForgeSnackbarStore";
import MyClipsStore from "./stores/MyClipsStore";
import AuthStore from "./stores/AuthStore";
import ExperienceStore from "./stores/ExperienceStore";
import NewRule from "sections/NewRule";
import EditRule from "sections/EditRule";

// The CSS injected by Material-UI to style a component has the highest specificity
// possible as the <link> is injected at the bottom of the <head> to ensure the
// components always render correctly. The following code allows us to ensure that
// the material classes are loaded before our styled-component classes.
// See https://material-ui-next.com/customization/css-in-js/#other-html-element for more info
import JssProvider from "react-jss/lib/JssProvider";
import { create } from "jss";
import { createGenerateClassName, jssPreset } from "@material-ui/core/styles";
import CreateCustomClipStore from "./stores/customClips/CreateCustomClipStore";
import HLSPlayerStore from "./stores/customClips/HLSPlayerStore";
import AppBar from "./components/AppBar";
import TypeAheadApi from "./httpClients/typeAheadApi";
import TypeaheadDropdownStore from "./stores/TypeaheadDropdownStore";
import { ToastContainer } from "react-toastify";
import VideoPlayer from "./components/VideoPlayer";
import CustomClipActionsStore from "./stores/customClips/CustomClipActionsStore";
import ConfirmationDialogStore from "./stores/ConfirmationDialogStore";
import ConfirmationDialog from "./components/ConfirmationDialog";
import MyCustomClipsStore from "./stores/customClips/MyCustomClipsStore";
import AllCustomClipsStore from "./stores/customClips/AllCustomClipsStore";
import GameMetadataStore from "./stores/customClips/GameMetadataStore";
import AdhocMetadataStore from "./stores/customClips/AdhocMetadataStore";
import WatermarkStore from "./stores/WatermarkStore";
import QuickerClipStore from "./stores/QuickerClipStore";
import QuickerClipForgeStore from "./stores/QuickerClipForgeStore";

const generateClassName = createGenerateClassName();
const jss = create(jssPreset());
jss.options.insertionPoint = document.getElementById("jss-insertion-point");

const theme = {
  black: {
    rgb: "rgb(0, 0, 0)"
  },
  blue: {
    rgb: "rgb(0, 45, 114)",
    rgbaLowOpacity: "rgba(0, 45, 114, 0.2)",
    rgbaMidOpacity: "rgba(0, 45, 114, 0.3)"
  },
  darkGray: {
    rgb: "rgb(216, 216, 216)",
    rgbaLowOpacity: "rgba(216, 216, 216, 0.3)",
    rgbaMidOpacity: "rgba(216, 216, 216, 0.5)"
  },
  lightGray: {
    rgb: "rgb(247, 247, 247)"
  },
  red: {
    rgb: "rgb(213, 0, 50)"
  },
  white: {
    rgb: "rgb(255, 255, 255)"
  }
};

const materialTheme = createTheme({
  typography: {
    useNextVariants: true
  },
  palette: {
    primary: {
      main: "#002D72"
    },
    secondary: {
      main: "#D50032"
    }
  }
});

const clippyApi = new ClippyApi();
const pitsApi = new PitsApi();
const sportyApi = new SportyApi();
const sportyGateway = new SportyGateway();
const statsApi = new StatsApi();
const cerebroApi = new CerebroApi();
const jaredApi = new JaredApi();
const typeAheadApi = new TypeAheadApi();

const experienceStore = new ExperienceStore();

const pitsMetadataStore = new PitsMetadataStore(pitsApi, experienceStore);
const sportyMetadataStore = new SportyMetadataStore(sportyApi);
const basicSearchStore = new BasicSearchStore(pitsApi, pitsMetadataStore);
const videoPlayerStore = new VideoPlayerStore();
const issueDialogStore = new IssueDialogStore(sportyApi);
const videoSearchStore = new VideoSearchStore(pitsApi, sportyApi, experienceStore);
const forgeSnackbarStore = new ForgeSnackbarStore();
const confirmationDialogStore = new ConfirmationDialogStore();
const typeaheadDropDownStore = new TypeaheadDropdownStore(
  typeAheadApi,
  pitsMetadataStore,
  experienceStore
);

const routerStore = new RouterStore(
  videoSearchStore,
  basicSearchStore,
  pitsMetadataStore
);

const history = syncHistoryWithStore(createBrowserHistory(), routerStore);

function onAuthRequired({ history }) {
  history.push("/login");
}

const auth = new Auth({
  history,
  issuer: process.env.REACT_APP_OKTA_ISSUER,
  clientId: process.env.REACT_APP_OKTA_CLIENT_ID,
  redirectUri: window.location.origin + "/implicit/callback",
  onAuthRequired: onAuthRequired
});

const authStore = new AuthStore(auth);

reaction(
  () => authStore.allowedExperiences,
  allowedExperiences => {
    if (allowedExperiences.length) {
      experienceStore.resolve(allowedExperiences);
    }
  }
);

authStore.getUser();

const sportsReferenceStore = new SportsReferenceStore(
  sportyGateway,
  experienceStore
);

const gameMetadataStore = new GameMetadataStore(statsApi, sportsReferenceStore);

const matchupsDialogStore = new MatchupsDialogStore(
  statsApi,
  basicSearchStore,
  pitsMetadataStore,
  routerStore,
  videoSearchStore,
  sportsReferenceStore
);

const ruleStore = new RuleStore(
  sportyApi,
  pitsApi,
  sportyMetadataStore,
  authStore
);
const rulesStore = new RulesStore(sportyApi, authStore);

const myClipsStore = new MyClipsStore(sportyApi);
const myCustomClipsStore = new MyCustomClipsStore(sportyApi);
const allCustomClipsStore = new AllCustomClipsStore(sportyApi);
const adhocMetadataStore = new AdhocMetadataStore(cerebroApi);
const hlsPlayerStore = new HLSPlayerStore();

const sendToForgeDialogStore = new SendToForgeDialogStore(
  pitsApi,
  clippyApi,
  jaredApi,
  typeaheadDropDownStore,
  gameMetadataStore,
  forgeSnackbarStore,
  authStore,
  experienceStore
);

const watermarkStore = new WatermarkStore(clippyApi);

const reelStore = new ReelStore(
  clippyApi,
  videoSearchStore,
  sportyApi,
  myClipsStore,
  basicSearchStore,
  authStore
);

const trimPlayDialogStore = new TrimPlayDialogStore(reelStore);

const createCustomClipStore = new CreateCustomClipStore(
  pitsApi,
  jaredApi,
  sportyApi,
  sportyGateway,
  statsApi,
  myCustomClipsStore,
  allCustomClipsStore,
  authStore
);

const customClipActionsStore = new CustomClipActionsStore(
  sportyApi,
  authStore,
  routerStore,
  confirmationDialogStore,
  myCustomClipsStore,
  allCustomClipsStore
);

const quickerClipForgeStore = new QuickerClipForgeStore(
  pitsApi,
  clippyApi,
  jaredApi,
  typeaheadDropDownStore,
  gameMetadataStore,
  forgeSnackbarStore,
  authStore,
  experienceStore
);

const quickerClipStore = new QuickerClipStore(
  quickerClipForgeStore,
  clippyApi,
  videoSearchStore,
  sportyApi,
  myClipsStore,
  basicSearchStore,
  authStore
);

(async function() {
  const accessToken = await auth.getAccessToken();
  if (accessToken) {
    await watermarkStore.initialize();

    // Ascertain permissions before loading any experience-specific data, so we
    // don't waste a request loading the wrong (unresolved) index. getUser resolves
    // the experience via the reaction above; pitsMetadataStore then loads Fields
    // against the correct index.
    await authStore.getUser();
    await pitsMetadataStore.initialize();

    // Check that the user has admin permissions before initializing the
    // SportyMetadataStore to prevent errors since most users are not able
    // to access Rules page.
    const canAccessRules = authStore.canAccessRules;
    if (canAccessRules) {
      await sportyMetadataStore.initialize();
    }
  }
})();

export default class App extends React.Component {
  render() {
    return (
      <JssProvider jss={jss} generateClassName={generateClassName}>
        <MuiThemeProvider theme={materialTheme}>
          <Provider
            basicSearchStore={basicSearchStore}
            pitsMetadataStore={pitsMetadataStore}
            sportyMetadataStore={sportyMetadataStore}
            sportsReferenceStore={sportsReferenceStore}
            reelStore={reelStore}
            quickerClipStore={quickerClipStore}
            quickerClipForgeStore={quickerClipForgeStore}
            routerStore={routerStore}
            ruleStore={ruleStore}
            rulesStore={rulesStore}
            gameMetadataStore={gameMetadataStore}
            adhocMetadataStore={adhocMetadataStore}
            videoPlayerStore={videoPlayerStore}
            issueDialogStore={issueDialogStore}
            videoSearchStore={videoSearchStore}
            matchupsDialogStore={matchupsDialogStore}
            trimPlayDialogStore={trimPlayDialogStore}
            sendToForgeDialogStore={sendToForgeDialogStore}
            forgeSnackbarStore={forgeSnackbarStore}
            authStore={authStore}
            experienceStore={experienceStore}
            myClipsStore={myClipsStore}
            myCustomClipsStore={myCustomClipsStore}
            allCustomClipsStore={allCustomClipsStore}
            createCustomClipStore={createCustomClipStore}
            typeaheadDropDownStore={typeaheadDropDownStore}
            customClipActionsStore={customClipActionsStore}
            confirmationDialogStore={confirmationDialogStore}
            watermarkStore={watermarkStore}
            hlsPlayerStore={hlsPlayerStore}
          >
            <ThemeProvider theme={theme}>
              <Router history={history}>
                <Security auth={auth}>
                  <div>
                    <ToastContainer />
                    <VideoPlayer />
                    <ConfirmationDialog />
                    <AppBar />
                    <SecureRoute exact path="/" component={VideoEditor} />
                    <SecureRoute exact path="/rules" component={Rules} />
                    <SecureRoute path="/rules/new" component={NewRule} />
                    <SecureRoute path="/rules/:id/edit" component={EditRule} />
                    <SecureRoute exact path="/clips" component={Clips} />
                    <SecureRoute
                      exact
                      path="/custom-clips"
                      component={CustomGameClipEditor}
                    />
                    <SecureRoute
                      exact
                      path="/custom-clips/adhoc"
                      component={CustomAdHocClipEditor}
                    />
                    <Route
                      path="/login"
                      render={() => (
                        <Login baseUrl={process.env.REACT_APP_OKTA_LOGIN_URL} />
                      )}
                    />
                    <Route
                      path="/implicit/callback"
                      component={ImplicitCallback}
                    />
                  </div>
                </Security>
              </Router>
            </ThemeProvider>
          </Provider>
        </MuiThemeProvider>
      </JssProvider>
    );
  }
}



// WEBPACK FOOTER //
// ./src/App.js